import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PackagePlus, ArrowDownToLine, ArrowUpFromLine, ShoppingCart, ListPlus, CalendarPlus, CalendarDays, FileDown, Layers } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { TASK_STATUS, PRIORITY, fmtDate, fmtTime, fmtBookingWhen, todayZurich } from "@/lib/format";
import { MapBadge, StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/Field";
import { ProductDialog } from "@/components/dialogs/ProductDialog";
import { MovementDialog } from "@/components/dialogs/MovementDialog";
import { ShoppingDialog } from "@/components/dialogs/ShoppingDialog";
import { TaskDialog } from "@/components/dialogs/TaskDialog";
import { EventDialog } from "@/components/dialogs/EventDialog";
import { MultiExitDialog } from "@/components/dialogs/MultiExitDialog";
import { PushBanner } from "@/components/PushControls";

const ACTIONS = [
  { id: "new-product", label: "Nuovo prodotto", icon: PackagePlus, perm: ["inventory", "create"], dialog: "product" },
  { id: "stock-in", label: "Registra entrata", icon: ArrowDownToLine, perm: ["movements", "create"], dialog: "in" },
  { id: "stock-out", label: "Registra uscita", icon: ArrowUpFromLine, perm: ["movements", "create"], dialog: "out" },
  { id: "multi-out", label: "Uscita multipla", icon: Layers, perm: ["movements", "create"], dialog: "multi" },
  { id: "add-shopping", label: "Aggiungi alla spesa", icon: ShoppingCart, perm: ["shopping", "create"], dialog: "shopping" },
  { id: "new-task", label: "Crea compito", icon: ListPlus, perm: ["tasks", "create"], dialog: "task" },
  { id: "new-event", label: "Crea evento", icon: CalendarPlus, perm: ["events", "create"], dialog: "event" },
  { id: "calendar", label: "Consulta calendario", icon: CalendarDays, perm: ["bookings", "view"], to: "/calendario" },
  { id: "export-pdf", label: "Esporta PDF", icon: FileDown, perm: ["reports", "export"], to: "/report" },
];

const Panel = ({ id, title, onAll, children }) => (
  <Card testId={`panel-${id}`}>
    <div className="flex items-center justify-between px-5 h-14 border-b border-slate-200">
      <h2 className="font-heading text-base font-semibold text-slate-900">{title}</h2>
      <button data-testid={`panel-${id}-see-all`} onClick={onAll} className="text-sm font-semibold text-primary hover:underline">Vedi tutto</button>
    </div>
    <ul className="divide-y divide-slate-100">{children}</ul>
  </Card>
);

export default function DashboardPage() {
  const { user, current, can } = useAuth();
  const nav = useNavigate();
  const [dialog, setDialog] = useState(null);
  const { data, reload } = useFetch("/dashboard");
  const close = (o) => !o && setDialog(null);
  const today = todayZurich();
  const hour = new Date().getHours();
  const hello = hour < 12 ? "Buongiorno" : hour < 18 ? "Buon pomeriggio" : "Buonasera";

  return (
    <>
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="dashboard-greeting">{hello}, {user?.name?.split(" ")[0]}</h1>
        <p className="text-slate-500 mt-1">{current?.name} · {fmtDate(today)}</p>
      </div>

      <PushBanner />

      <section data-testid="quick-actions">
        <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3">Azioni rapide</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {ACTIONS.filter((a) => can(...a.perm)).map(({ id, label, icon: Icon, dialog: d, to }, i) => (
            <button key={id} data-testid={`quick-action-${id}`} onClick={() => (to ? nav(to) : setDialog(d))}
              className={`flex items-center gap-3 rounded-xl px-4 min-h-[60px] text-left text-[15px] font-semibold transition-colors ${i < 3 ? "bg-primary text-primary-foreground hover:opacity-90" : "bg-white border border-slate-200 text-slate-800 hover:border-primary hover:text-primary"}`}>
              <Icon className="h-5 w-5 shrink-0" /> {label}
            </button>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {can("tasks", "view") && (
          <Panel id="my-tasks" title="I miei compiti" onAll={() => nav("/compiti")}>
            {data && data.my_tasks.length === 0 && <li className="px-5 py-6 text-slate-500 text-sm">Nessun compito aperto assegnato a te.</li>}
            {data?.my_tasks.map((t) => (
              <li key={t.id}>
                <button data-testid={`dash-task-${t.id}`} onClick={() => nav(`/compiti?t=${t.id}`)} className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-slate-50">
                  <div className="min-w-0">
                    <div className="text-[15px] font-medium text-slate-900 truncate">{t.title}</div>
                    <div className={`text-sm ${t.due_date && t.due_date < today ? "text-red-700 font-semibold" : "text-slate-500"}`}>{t.due_date ? `Scadenza ${fmtDate(t.due_date)}` : "Senza scadenza"}</div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    {t.due_date && t.due_date < today ? <StatusBadge label="Scaduto" tone="danger" /> : t.priority !== "normal" && <MapBadge map={PRIORITY} value={t.priority} />}
                    <MapBadge map={TASK_STATUS} value={t.status} />
                  </div>
                </button>
              </li>
            ))}
          </Panel>
        )}
        {can("bookings", "view") && (
          <Panel id="bookings" title="Prossime prenotazioni" onAll={() => nav("/sale")}>
            {data?.pending_bookings > 0 && (
              <li><button data-testid="dash-pending-bookings" onClick={() => nav("/sale?status=pending")} className="w-full px-5 py-3 text-left text-sm font-semibold text-amber-900 bg-amber-50 hover:bg-amber-100">{data.pending_bookings} richieste in attesa di approvazione</button></li>
            )}
            {data && data.upcoming_bookings.length === 0 && <li className="px-5 py-6 text-slate-500 text-sm">Nessuna prenotazione confermata in arrivo.</li>}
            {data?.upcoming_bookings.map((b) => (
              <li key={b.id} data-testid={`dash-booking-${b.id}`} className="flex items-center justify-between gap-3 px-5 py-3">
                <div className="min-w-0">
                  <div className="text-[15px] font-medium text-slate-900 truncate">{b.room_name} · {b.event_type || b.requester_name}</div>
                  <div className="text-sm text-slate-500">{fmtBookingWhen(b)}</div>
                </div>
                <StatusBadge label="Confermata" tone="success" />
              </li>
            ))}
          </Panel>
        )}
      </div>

      <ProductDialog open={dialog === "product"} onOpenChange={close} onSaved={(p) => nav(`/inventario/${p.id}`)} />
      <MovementDialog open={dialog === "in"} mode="in" onOpenChange={close} onSaved={reload} />
      <MovementDialog open={dialog === "out"} mode="out" onOpenChange={close} onSaved={reload} />
      <MultiExitDialog open={dialog === "multi"} onOpenChange={close} onSaved={reload} />
      <ShoppingDialog open={dialog === "shopping"} onOpenChange={close} onSaved={reload} />
      <TaskDialog open={dialog === "task"} onOpenChange={close} onSaved={reload} />
      <EventDialog open={dialog === "event"} onOpenChange={close} onSaved={(e) => nav(`/eventi/${e.id}`)} />
    </>
  );
}
